import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, MapPin, Camera, UserCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface HabitLog {
  id: string;
  completed_at: string;
  evidence_url: string | null;
  notes: string | null;
  user_id: string;
  confirmer_name?: string;
}

interface HabitHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  habitId: string;
  habitName: string;
  evidenceType: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
}

export const HabitHistoryModal = ({
  open,
  onOpenChange,
  habitId,
  habitName,
  evidenceType,
  locationLat,
  locationLng,
}: HabitHistoryModalProps) => {
  const [logs, setLogs] = useState<HabitLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const fetchLogs = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("habit_logs")
        .select("*")
        .eq("habit_id", habitId)
        .order("completed_at", { ascending: false });

      if (!error && data) {
        setLogs(data);
      }
      setLoading(false);
    };
    fetchLogs();
  }, [open, habitId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[100dvh] h-[100dvh] sm:h-[90vh] flex flex-col p-0 gap-0 bg-background border-border">
        <DialogHeader className="p-4 pb-2 border-b border-border shrink-0">
          <DialogTitle className="text-lg font-display text-center pr-8">
            Histórico — {habitName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              Nenhum registro ainda.
            </p>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                className="rounded-xl border border-border bg-card p-4 space-y-3"
              >
                {/* Date */}
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">
                    {format(new Date(log.completed_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(log.completed_at), "HH:mm")}
                  </p>
                </div>

                {/* Photo evidence */}
                {evidenceType === "photo" && log.evidence_url && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Camera className="h-3 w-3" />
                      <span>Foto de evidência</span>
                    </div>
                    <img
                      src={log.evidence_url}
                      alt="Evidência"
                      className="w-full h-40 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setExpandedImage(log.evidence_url)}
                    />
                  </div>
                )}

                {/* Location evidence */}
                {evidenceType === "location" && locationLat && locationLng && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span>Verificado por localização</span>
                    </div>
                    <a
                      href={`https://www.google.com/maps?q=${locationLat},${locationLng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg overflow-hidden border border-border"
                    >
                      <img
                        src={`https://maps.googleapis.com/maps/api/staticmap?center=${locationLat},${locationLng}&zoom=15&size=400x150&markers=color:green%7C${locationLat},${locationLng}&key=`}
                        alt="Localização"
                        className="w-full h-[120px] object-cover bg-muted"
                        onError={(e) => {
                          // Fallback if no API key
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentElement!.innerHTML = `
                            <div class="flex items-center justify-center h-[80px] bg-muted text-muted-foreground text-sm gap-2">
                              <span>📍</span> Ver no mapa
                            </div>
                          `;
                        }}
                      />
                    </a>
                  </div>
                )}

                {/* Confirmation evidence */}
                {evidenceType === "confirmation" && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <UserCheck className="h-3 w-3" />
                    <span>Confirmado por outro membro</span>
                  </div>
                )}

                {/* Time evidence */}
                {evidenceType === "time" && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>⏰</span>
                    <span>Registrado no horário programado</span>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Expanded image overlay */}
        {expandedImage && (
          <div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setExpandedImage(null)}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/20"
              onClick={() => setExpandedImage(null)}
            >
              <X className="h-6 w-6" />
            </Button>
            <img
              src={expandedImage}
              alt="Evidência ampliada"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
